/**
 * ファイルアップロード用モジュール
 */

import { ThunkAction } from 'redux-thunk';

import { UploadFileForHighlight, uploadForHighlight } from '../services/contractApi';
export { UploadFileForHighlight } from '../services/contractApi';
import fillArray from '../utils/fillArray';

export type UploadFileState = 'none' | 'uploading';
export type UploadFilePattern = 'AUTO' | 'D' ;
export const uploadFilePatterns: UploadFilePattern[] = ['AUTO', 'D'];

export interface UploadForHighlightState {
  files: (UploadFileForHighlight | null)[];
  fileStates: UploadFileState[];
  filePatterns: UploadFilePattern[];
  messages: string[];
  status: number;
}

const UPLOAD_COUNT = 2;
const initialForHighlightState: UploadForHighlightState = {
  files: fillArray(Array(UPLOAD_COUNT), null),
  fileStates: fillArray(Array(UPLOAD_COUNT), 'none'),
  filePatterns: fillArray(Array(UPLOAD_COUNT), uploadFilePatterns[0]),
  messages: [],
  status: 0,
};

const START_UPLOAD_FOR_HIGHLIGHT = 'START_UPLOAD_FOR_HIGHLIGHT';
type START_UPLOAD_FOR_HIGHLIGHT = typeof START_UPLOAD_FOR_HIGHLIGHT;

const FINISH_UPLOAD_FOR_HIGHLIGHT = 'FINISH_UPLOAD_FOR_HIGHLIGHT';
type FINISH_UPLOAD_FOR_HIGHLIGHT = typeof FINISH_UPLOAD_FOR_HIGHLIGHT;

const FAIL_UPLOAD_FOR_HIGHLIGHT = 'FAIL_UPLOAD_FOR_HIGHLIGHT';
type FAIL_UPLOAD_FOR_HIGHLIGHT = typeof FAIL_UPLOAD_FOR_HIGHLIGHT;

const CLEAR_UPLOAD_FILE_FOR_HIGHLIGHT = 'CLEAR_UPLOAD_FILE_FOR_HIGHLIGHT';
type CLEAR_UPLOAD_FILE_FOR_HIGHLIGHT = typeof CLEAR_UPLOAD_FILE_FOR_HIGHLIGHT;

const CLEAR_UPLOAD_MESSAGES_FOR_HIGHLIGHT = 'CLEAR_UPLOAD_MESSAGES_FOR_HIGHLIGHT';
type CLEAR_UPLOAD_MESSAGES_FOR_HIGHLIGHT = typeof CLEAR_UPLOAD_MESSAGES_FOR_HIGHLIGHT;

const SET_FILE_PATTERN_FOR_HIGHLIGHT = 'SET_FILE_PATTERN_FOR_HIGHLIGHT';
type SET_FILE_PATTERN_FOR_HIGHLIGHT = typeof SET_FILE_PATTERN_FOR_HIGHLIGHT;

const CLEAR_UPLOAD_FOR_HIGHLIGHT = 'CLEAR_UPLOAD_FOR_HIGHLIGHT';
type CLEAR_UPLOAD_FOR_HIGHLIGHT = typeof CLEAR_UPLOAD_FOR_HIGHLIGHT;

interface StartUploadForHighlight {
  type: START_UPLOAD_FOR_HIGHLIGHT;
  index: number;
}

interface FinishUploadForHighlight {
  type: FINISH_UPLOAD_FOR_HIGHLIGHT;
  index: number;
  file: UploadFileForHighlight;
}

interface FailUploadForHighlight {
  type: FAIL_UPLOAD_FOR_HIGHLIGHT;
  index: number;
  messages: string[];
  status: number;
}

interface ClearUploadFileForHighlight {
  type: CLEAR_UPLOAD_FILE_FOR_HIGHLIGHT;
  index: number;
}

interface ClearUploadForHighlight {
  type: CLEAR_UPLOAD_FOR_HIGHLIGHT;
}

interface ClearUploadMessagesForHighlight {
  type: CLEAR_UPLOAD_MESSAGES_FOR_HIGHLIGHT;
}

interface SetFilePatternForHighlight {
  type: SET_FILE_PATTERN_FOR_HIGHLIGHT;
  index: number;
  pattern: UploadFilePattern;
}

export type ContractAction = StartUploadForHighlight | FinishUploadForHighlight | FailUploadForHighlight |
  ClearUploadFileForHighlight | ClearUploadMessagesForHighlight | SetFilePatternForHighlight | ClearUploadForHighlight;

type PostUpload =
  ThunkAction<void, UploadForHighlightState, undefined, ContractAction>;

export function startUploadForHighlight(index: number): StartUploadForHighlight {
  return {
    index,
    type: START_UPLOAD_FOR_HIGHLIGHT,
  };
}

export function finishUploadForHighlight(index: number, file: UploadFileForHighlight): FinishUploadForHighlight {
  return {
    index,
    file,
    type: FINISH_UPLOAD_FOR_HIGHLIGHT,
  };
}

export function failUploadForHighlight(index: number, messages: string[], status: number): FailUploadForHighlight {
  return {
    index,
    messages,
    status,
    type: FAIL_UPLOAD_FOR_HIGHLIGHT,
  };
}

export function clearUploadFileForHighlight(index: number): ClearUploadFileForHighlight {
  return {
    index,
    type: CLEAR_UPLOAD_FILE_FOR_HIGHLIGHT,
  };
}

export function clearUploadForHighlight(): ClearUploadForHighlight {
  return {
    type: CLEAR_UPLOAD_FOR_HIGHLIGHT,
  };
}

export function clearUploadMessagesForHighlight(): ClearUploadMessagesForHighlight {
  return {
    type: CLEAR_UPLOAD_MESSAGES_FOR_HIGHLIGHT,
  };
}

export function setUploadFilePatternForHighlight(index: number, pattern: UploadFilePattern): SetFilePatternForHighlight {
  return {
    index,
    pattern,
    type: SET_FILE_PATTERN_FOR_HIGHLIGHT,
  };
}

export function postUpload(index: number, file: File): PostUpload {
  return (dispatch) => {
    dispatch(startUploadForHighlight(index));
    uploadForHighlight(file).then((res) => {
      const { status, result, messages, status_code } = res;
      if (status === 200 && result) {
        dispatch(finishUploadForHighlight(index, result));
      } else {
        dispatch(failUploadForHighlight(index, messages, status_code));
      }
    }).catch((err) => {
      dispatch(failUploadForHighlight(index, [String(err)], 500));
    });
  };
}

export default function reducer(
  state: UploadForHighlightState | undefined,
  action: ContractAction,
): UploadForHighlightState {
  if (!state) return initialForHighlightState;
  switch (action.type) {
    case START_UPLOAD_FOR_HIGHLIGHT: {
      const newStates = [...state.fileStates];
      newStates[action.index] = 'uploading';
      return { ...state, fileStates: newStates };
    }
    case FINISH_UPLOAD_FOR_HIGHLIGHT: {
      const newFiles = [...state.files];
      const newStates = [...state.fileStates];
      newFiles[action.index] = action.file;
      newStates[action.index] = 'none';
      return { ...state, files: newFiles, fileStates: newStates, messages: [] };
    }
    case FAIL_UPLOAD_FOR_HIGHLIGHT: {
      const newStates = [...state.fileStates];
      const { status } = action;
      newStates[action.index] = 'none';
      return { ...state, status, fileStates: newStates, messages: action.messages };
    }
    case CLEAR_UPLOAD_FILE_FOR_HIGHLIGHT: {
      const newFiles = [...state.files];
      newFiles[action.index] = null;
      return { ...state, files: newFiles };
    }
    case CLEAR_UPLOAD_FOR_HIGHLIGHT: {
      return initialForHighlightState;
    }
    case CLEAR_UPLOAD_MESSAGES_FOR_HIGHLIGHT: {
      return { ...state, messages: [] };
    }
    case SET_FILE_PATTERN_FOR_HIGHLIGHT: {
      const newPatterns = [...state.filePatterns];
      newPatterns[action.index] = action.pattern;
      return { ...state, filePatterns: newPatterns };
    }
  }
  return state;
}



// WEBPACK FOOTER //
// ./src/modules/uploadForHighlight.ts